import { createRootRouteWithContext, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'
import { ToastProvider } from '@/providers/ToastProvider'
import { captureEvent, isPostHogEnabled } from '@/lib/posthog'
import { AnalyticsEvents } from '@/lib/analytics-events'
import type { RouterContext } from '@/types/router.types'
import { usePushNotificationEnforcement } from '@/hooks/usePushNotificationEnforcement'
import { usePushNotificationListeners } from '@/hooks/usePushNotificationListeners'
import { PushNotificationEnforcementModal } from '@/components/common/PushNotificationEnforcementModal/PushNotificationEnforcementModal'

function PostHogRouteTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => {
    if (!isPostHogEnabled()) return
    captureEvent(AnalyticsEvents.PAGE_VIEW, {
      $current_url: window.location.href,
      path: pathname,
    })
  }, [pathname])

  return null
}

function PushNotificationHandler() {
  const { showModal, openSettings, dismiss } = usePushNotificationEnforcement()
  usePushNotificationListeners()

  if (showModal) {
    return <PushNotificationEnforcementModal onOpenSettings={openSettings} onDismiss={dismiss} />
  }

  return null
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: function RootComponent() {
    const pathname = useRouterState({ select: (s) => s.location.pathname })
    const isDisplayMode = pathname.startsWith('/display')

    return (
      <ToastProvider>
        {!isDisplayMode && <PostHogRouteTracker />}
        {!isDisplayMode && <PushNotificationHandler />}
        <Outlet />
      </ToastProvider>
    )
  },
  notFoundComponent: function NotFound() {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Page not found</h1>
        <p>The page you were looking for doesn't exist.</p>
        <Link to="/dashboard">Go to dashboard</Link>
      </div>
    )
  },
})
